import { useTranslation } from "react-i18next";
import { LeftOutlined, RightOutlined, SearchOutlined } from '@ant-design/icons';
import { Button, Col, Input, Modal, Pagination, Row, Select, Table, Typography } from 'antd';
import React, { useEffect, useMemo, useState } from 'react';
import { getAllMultiLevelUniversities } from '../../service/learning-material';
import { permissionOptions } from '../../utils/sharePermission';
import FormItemDropdown from '../form-dropdown/FormItemDropdown';
import "./AddUniversityPermissionModal.scss";
const { Search } = Input;
const { Option } = Select;

export interface UniversityNode {
	id: number;
	uuid?: string;
	name: string;
	children?: UniversityNode[] | null;
	isDaiHoc?: boolean;
	universityId?: number;
	nameEn?: string;
	code?: string;
	logo?: string;
	canManage?: boolean;
}

interface Props {
	open: boolean;
	onCancel: () => void;
	onOk: (
		arr: {
			uuid: string;
			name: string;
			permission: string;
			parentUuid: string | null;
			isDaiHoc?: boolean;
			code?: string;
		}[]
	) => void;
	defaultSelected?: { uuid: string; permission: string }[];
	authorizedUniversities?: {
		uuid?: string;
		enterpriseId?: string;
		name: string;
		permission: string
	}[];
}

const AddUniversityPermissionModal: React.FC<Props> = ({
	open,
	onCancel,
	onOk,
	defaultSelected = [],
	authorizedUniversities = [],
}) => {
  const { t } = useTranslation();
	const [tree, setTree] = useState<UniversityNode[]>([]);
	const [loading, setLoading] = useState(false);
	const [totalRecords, setTotalRecords] = useState(0);
	const [selected, setSelected] = useState<React.Key[]>([]);
	const [permMap, setPermMap] = useState<Record<string, string>>({});
	const [globalPerm, setGlobalPerm] = useState<string>('2');
	const [keyword, setKeyword] = useState('');
	const [page, setPage] = useState(1);
	const [pageSize, setPageSize] = useState(10);
	const [totalAvailableSchools, setTotalAvailableSchools] = useState(0);
	const [currentPageSchools, setCurrentPageSchools] = useState(0);
	const [allNodesMap, setAllNodesMap] = useState<Map<string, UniversityNode>>(new Map());

	const authorizedUuids = useMemo(() => {
		const uuids = new Set<string>();
		authorizedUniversities?.forEach(u => {
			if (u.uuid) uuids.add(u.uuid);
			if (u.enterpriseId) uuids.add(u.enterpriseId);
		});
		return uuids;
	}, [authorizedUniversities]);

	useEffect(() => {
		if (open && defaultSelected.length > 0) {
			setSelected(defaultSelected.map(item => item.uuid));

			const newPermMap: Record<string, string> = {};
			defaultSelected.forEach(item => {
				newPermMap[item.uuid] = item.permission;
			});
			setPermMap(newPermMap);
		}
	}, [open, defaultSelected]);

	// Hàm build map của tất cả nodes để tra cứu nhanh
	const buildNodesMap = (nodes: UniversityNode[], map: Map<string, UniversityNode>) => {
		nodes.forEach(node => {
			if (node.uuid) {
				map.set(node.uuid, node);
			}
			if (node.children && Array.isArray(node.children) && node.children.length > 0) {
				buildNodesMap(node.children, map);
			}
		});
	};

	// Hàm đếm tất cả schools (isDaiHoc === false) trong danh sách nodes
	const countAllSchools = (nodes: UniversityNode[]): number => {
		let count = 0;
		const traverse = (nodeList: UniversityNode[]) => {
			nodeList.forEach(node => {
				if (node.isDaiHoc === false) {
					count++;
				}
				if (node.children && Array.isArray(node.children) && node.children.length > 0) {
					traverse(node.children);
				}
			});
		};
		traverse(nodes);
		return count;
	};

	// Hàm đếm schools khả dụng (chưa được authorized và chưa selected)
	const countAvailableSchools = (nodes: UniversityNode[]): number => {
		let count = 0;
		const traverse = (nodeList: UniversityNode[]) => {
			nodeList.forEach(node => {
				if (node.isDaiHoc === false &&
					!authorizedUuids.has(node.uuid || '') &&
					!selected.includes(node.uuid || '')) {
					count++;
				}
				if (node.children && Array.isArray(node.children) && node.children.length > 0) {
					traverse(node.children);
				}
			});
		};
		traverse(nodes);
		return count;
	};

	// Fetch tất cả data để tính tổng số schools khả dụng
	const fetchTotalAvailableSchools = async () => {
		try {
			const res = await getAllMultiLevelUniversities({
				page: 1,
				size: 9999, // Lấy tất cả data
				keyword: keyword
			});
			const allNodes: UniversityNode[] = res?.data?.data?.content ?? [];

			// Build map cho tất cả nodes
			const newMap = new Map<string, UniversityNode>();
			buildNodesMap(allNodes, newMap);
			setAllNodesMap(newMap);

			const availableCount = countAvailableSchools(allNodes);
			setTotalAvailableSchools(availableCount);
		} catch (error) {
			console.error('Error fetching total schools:', error);
		}
	};

	// Fetch data cho trang hiện tại
	const fetchCurrentPageData = async () => {
		setLoading(true);
		try {
			const payload = { page, size: pageSize, keyword };
			const res = await getAllMultiLevelUniversities(payload);
			const list: UniversityNode[] = res?.data?.data?.content ?? [];
			setTree(list);
			setTotalRecords(res?.data?.data?.totalElements || 0);

			// Đếm số schools trong trang hiện tại
			const schoolsInCurrentPage = countAllSchools(list);
			setCurrentPageSchools(schoolsInCurrentPage);
		} finally {
			setLoading(false);
		}
	};

	// Effect khi mở modal hoặc thay đổi keyword
	useEffect(() => {
		if (!open) return;

		// Fetch tổng số schools khả dụng
		fetchTotalAvailableSchools();
	}, [open, keyword, authorizedUuids, selected]);

	// Effect khi thay đổi page, pageSize hoặc keyword
	useEffect(() => {
		if (!open) return;

		fetchCurrentPageData();
	}, [open, page, pageSize, keyword]);

	const handleSearch = () => {
		setPage(1); // Reset về trang 1 khi search
	};

	const attachPermAndIndex = (
		nodes: UniversityNode[],
		parentIndex: string = '',
		isRoot: boolean = true
	): any[] => {
		const result: any[] = [];
		let baseIndex = 0;

		nodes.forEach((node, index) => {
			const currentIndex = baseIndex + index + 1;
			const displayIndex = parentIndex
				? `${parentIndex}.${currentIndex}`
				: currentIndex.toString();

			const isDisabled = node.uuid ? authorizedUuids.has(node.uuid) : false;

			const processedNode: any = {
				...node,
				key: node.uuid,
				numericalOrder: displayIndex,
				permission: permMap[node.uuid || ''] || globalPerm,
				disabled: isDisabled,
			};

			if (node.children && Array.isArray(node.children) && node.children.length > 0) {
				processedNode.children = attachPermAndIndex(
					node.children,
					displayIndex,
					false
				);
			} else {
				delete processedNode.children;
			}

			result.push(processedNode);
		});

		return result;
	};

	const dataSource = useMemo(() => {
		return attachPermAndIndex(tree, '', true);
	}, [tree, permMap, globalPerm, authorizedUuids]);

	const columns = [
		{
			title: "STT",
			dataIndex: "numericalOrder",
			key: "numericalOrder",
			width: 80,
			fixed: 'left' as const,
			render: (_: any, record: any) => {
				return (
					<span className="table-order-header">
						{record?.numericalOrder}
					</span>
				);
			},
		},
		{
			title: t("Tên đơn vị đào tạo", "Tên đơn vị đào tạo"),
			dataIndex: 'name',
			key: 'name',
			render: (text: string, record: any) => {
				const isDisabled = record.disabled;
				const isCurrentlySelected = selected.includes(record.key);

				return (
					<div style={{ opacity: isDisabled ? 0.5 : 1 }}>
						<Typography.Text strong={!isDisabled || isCurrentlySelected}>
							{text}
						</Typography.Text>
						{record.code && (
							<Typography.Text
								type="secondary"
								style={{ fontSize: 12, marginLeft: 8 }}
							>
								({record.code})
							</Typography.Text>
						)}
						{isDisabled && !isCurrentlySelected && (
							<div style={{ color: '#ff4d4f', fontSize: 12, marginTop: 4 }}>
								{t("Đã được chia sẻ", "Đã được chia sẻ")}
							</div>
						)}
					</div>
				);
			},
		},
	];

	const rowSelection = {
		selectedRowKeys: selected,
		onChange: (keys: React.Key[], selectedRows: any[]) => {
			// Lấy danh sách các keys hiện tại trong trang này
			const currentPageKeys = new Set<React.Key>();
			const getAllKeysFromNode = (node: any) => {
				currentPageKeys.add(node.key);
				if (node.children && node.children.length > 0) {
					node.children.forEach((child: any) => getAllKeysFromNode(child));
				}
			};
			dataSource.forEach((node: any) => getAllKeysFromNode(node));

			// Giữ lại các selection từ các trang khác
			const selectionsFromOtherPages = selected.filter(key => !currentPageKeys.has(key));

			// Kết hợp với selection mới từ trang hiện tại
			const newSelection = [...selectionsFromOtherPages, ...keys];

			setSelected(newSelection);
		},
		checkStrictly: false,
		getCheckboxProps: (record: any) => ({
			disabled: record.disabled,
		}),
	};

	const findNode = (
		list: UniversityNode[],
		uuid: string,
		parent: UniversityNode | null = null
	): { node: UniversityNode; parentUuid: string | null } | undefined => {
		for (const item of list) {
			if (item.uuid === uuid) {
				return { node: item, parentUuid: parent?.uuid ?? null };
			}
			if (item.children && item.children.length > 0) {
				const found = findNode(item.children, uuid, item);
				if (found) return found;
			}
		}
		return undefined;
	};

	const collectParentNodes = (
		nodeUuid: string,
		results: Map<string, { node: UniversityNode; parentUuid: string | null }>
	) => {
		const found = findNode(tree, nodeUuid);
		if (found && found.parentUuid) {
			const parentFound = findNode(tree, found.parentUuid);
			if (parentFound && !results.has(parentFound.node.uuid || '')) {
				results.set(parentFound.node.uuid || '', parentFound);
				collectParentNodes(parentFound.node.uuid || '', results);
			}
		}
	};

	const handleOk = () => {
		const results = new Map<string, { node: UniversityNode; parentUuid: string | null }>();

		// Lấy tất cả data để xử lý selection từ nhiều trang
		getAllMultiLevelUniversities({
			page: 1,
			size: 9999,
			keyword: keyword
		}).then(res => {
			const allNodes: UniversityNode[] = res?.data?.data?.content ?? [];

			selected.forEach((uuid) => {
				const found = findNode(allNodes, String(uuid));
				if (found && found.node.uuid) {
					results.set(found.node.uuid, found);
					// Collect parent nodes
					const collectParentsFromAllNodes = (nodeUuid: string) => {
						const parentFound = findNode(allNodes, nodeUuid);
						if (parentFound && parentFound.parentUuid) {
							const grandParentFound = findNode(allNodes, parentFound.parentUuid);
							if (grandParentFound && !results.has(grandParentFound.node.uuid || '')) {
								results.set(grandParentFound.node.uuid || '', grandParentFound);
								collectParentsFromAllNodes(grandParentFound.node.uuid || '');
							}
						}
					};
					collectParentsFromAllNodes(found.node.uuid);
				}
			});

			const finalResults = Array.from(results.values()).map(({ node, parentUuid }) => ({
				uuid: node.uuid || '',
				name: node.name,
				permission: permMap[node.uuid || ''] || globalPerm,
				parentUuid,
				isDaiHoc: node.isDaiHoc || false,
				code: node.code || ''
			}));

			onOk(finalResults);
		});
	};

	useEffect(() => {
		if (!open) {
			setSelected([]);
			setPermMap({});
			setKeyword('');
			setPage(1);
			setGlobalPerm('2');
			setTotalAvailableSchools(0);
			setCurrentPageSchools(0);
			setAllNodesMap(new Map());
		}
	}, [open]);

	// Đếm số schools đã chọn (từ tất cả các trang)
	const selectedSchoolsCount = useMemo(() => {
		let count = 0;
		selected.forEach(uuid => {
			const node = allNodesMap.get(String(uuid));
			if (node && node.isDaiHoc === false) {
				count++;
			}
		});
		return count;
	}, [selected, allNodesMap]);

	return (
		<Modal
			destroyOnClose
			width={1000}
			title={t("Thêm đơn vị đào tạo", "Thêm đơn vị đào tạo")}
			open={open}
			onCancel={onCancel}
			onOk={handleOk}
			okText={t("Thêm", "Thêm")}
			cancelText={t("Huỷ", "Huỷ")}
			centered
			className="add-university-permission-modal"
			okButtonProps={{
				disabled: selected.length === 0
			}}
		>
			<Row gutter={16} style={{ marginBottom: 16 }}>
				<Col span={12}>
					<Search
						placeholder={t("Tìm kiếm theo tên hoặc mã trường", "Tìm kiếm theo tên hoặc mã trường")}
						allowClear
						value={keyword}
						onChange={(e) => setKeyword(e.target.value)}
						onSearch={handleSearch}
						onPressEnter={handleSearch}
						style={{ width: '100%' }}
						enterButton={
							<Button
								icon={<SearchOutlined />}
								loading={loading}
							>
							</Button>
						}
					/>
				</Col>

				<Col span={12}>
					<div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
						<Typography.Text>{t("Quyền mặc định:", "Quyền mặc định:")}</Typography.Text>
						<FormItemDropdown
							description
							value={globalPerm}
							onChange={setGlobalPerm}
							options={permissionOptions
								.filter((p) => p.id === '2' || p.id === '3')}
							style={{ width: "200px" }}
						/>
					</div>
				</Col>
			</Row>

			<div style={{ marginBottom: 8 }}>
				<strong>{t("Chọn đơn vị đào tạo", "Chọn đơn vị đào tạo")}</strong>
				{selected.length > 0 && (
					<span style={{ marginLeft: 8, color: '#1890ff' }}>
						({selectedSchoolsCount} {t("đơn vị đào tạo đã chọn)", "đơn vị đào tạo đã chọn)")}
					</span>
				)}
				<span style={{ float: 'right', fontSize: 13, color: '#888' }}>
					{totalAvailableSchools} {t("đơn vị đào tạo khả dụng", "đơn vị đào tạo khả dụng")}
				</span>
			</div>

			<div className="table-wrapper">
				<Table
					columns={columns}
					dataSource={dataSource}
					rowSelection={rowSelection}
					rowKey="uuid"
					expandable={{
						childrenColumnName: 'children',
						rowExpandable: (record) => !!(record.children && record.children.length > 0),
						expandRowByClick: true,
						defaultExpandAllRows: false
					}}
					size="small"
					pagination={false}
					loading={loading}
					scroll={{ x: 700, y: 400 }}
					locale={{
						emptyText: (
							<span className="empty-table">
								<p>
									<img
										className="image-empty-data"
										src="/images/empty-img-gray.png"
										alt="empty"
									/>
								</p>
								<p className="nodata-text">
									{keyword ? `Không tìm thấy kết quả với từ khóa "${keyword}"` : t("Không có dữ liệu", "Không có dữ liệu")}
								</p>
							</span>
						),
						triggerDesc: t("Sắp xếp theo thứ tự Z-A", "Sắp xếp theo thứ tự Z-A"),
						triggerAsc: t("Sắp xếp thứ tự A-Z", "Sắp xếp thứ tự A-Z"),
						cancelSort: t("Huỷ sắp xếp", "Huỷ sắp xếp"),
					}}
				/>

				<Pagination
					total={totalRecords}
					current={page}
					pageSize={pageSize}
					showSizeChanger={false}
					showQuickJumper={false}
					className="pagination-table"
					onChange={(p, s) => {
						setPage(p);
						setPageSize(s || pageSize);
					}}
					showTotal={(total) =>
						selected.length
							? `${t("Đã chọn", "Đã chọn")} ${selectedSchoolsCount} / ${t("Tổng", "Tổng")} ${currentPageSchools} ${t("đơn vị đào tạo", "đơn vị đào tạo")}`
							: `${currentPageSchools} ${t("đơn vị đào tạo", "đơn vị đào tạo")}`
					}
					itemRender={(_, type, original) =>
						type === "prev" ? <LeftOutlined /> :
							type === "next" ? <RightOutlined /> : original
					}
					locale={{
						items_per_page: "/ trang",
						jump_to: t("Đi đến trang", "Đi đến trang"),
						page: "",
					}}
				/>
			</div>
		</Modal>
	);
};

export default AddUniversityPermissionModal;